import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { formatPhoneNumber } from '../utils/format';
import { downloadRecordFile, downloadStereoRecordFile } from '../api/audio';
import { buildRecordFileName, buildStereoRecordFileName, isRecordActionDisabled, saveBlobAsFile } from '../utils/recordDownload';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getCallStatusLabel } from '../utils/callStatus';
import { Button } from '../styles/common/Button.styled';
import * as T from '../styles/common/table.styled';
import { callStatusTone } from '../styles/consoleTheme';
import PageHeader from '../components/common/PageHeader';
import PageState from '../components/common/PageState';
import RealContactName from '../components/common/RealContactName';

// ─── Styled Components ───────────────────────────────────────────────────────

const PageContainer = styled.div`display: flex; flex-direction: column; gap: 20px; min-width: 0; max-width: 1200px;`;



const BackButton = styled(Button).attrs({ $variant: "ghost", $size: "sm" })``;



const Card = styled.div`display: flex; flex-direction: column; gap: 24px; min-width: 0;`;

const SectionTitle = styled.h2`font-size: 14px; font-weight: 600; line-height: 20px; color: ${({ theme }) => theme.color.text_color_100}; margin: 0 0 12px;`;

const HeroSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const HeroHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CallRoute = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 28px minmax(0, 1fr);
  align-items: stretch;
  gap: 8px;
`;

const CallInfoLayout = styled.div`display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.8fr); gap: 24px; @media(max-width: 1200px) { grid-template-columns: 1fr; }`;

const CallInfoLeft = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 12px;
`;

const ParticipantBlock = styled.div`display: flex; flex-direction: column; gap: 6px; min-width: 0; padding: 16px 0;`;

const ParticipantLabel = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.color.text_color_300};
  font-weight: 700;
`;

const ParticipantName = styled.span`font-size: 18px; font-weight: 600; color: ${({ theme }) => theme.color.text_color_100}; overflow-wrap: anywhere;`;

const OriginalName = styled.small`font-size: 12px; color: ${({ theme }) => theme.color.text_color_300}; overflow-wrap: anywhere;`;

const ParticipantIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ParticipantPhone = styled.span`font-size: 13px; color: ${({ theme }) => theme.color.text_color_200}; font-variant-numeric: tabular-nums;`;

const RouteConnector = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    border-top: 1px solid ${({ theme }) => theme.color.primary_300};
  }
`;

const RouteArrow = styled.div`
  position: relative;
  z-index: 1;
  padding: 0 2px;
  background: ${({ theme }) => theme.color.primary_100};
  color: ${({ theme }) => theme.color.text_color_400};
  font-size: 16px;
  font-weight: 800;
  line-height: 1;
`;

const CallFacts = styled.div`overflow-x: auto; border-left: 1px solid ${({ theme }) => theme.color.primary_300}; padding-left: 12px; @media(max-width:1200px) { border-left: 0; padding-left: 0; }`;

const CallFactsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const CallFactsHead = styled.th`width: 104px; padding: 9px 12px; color: ${({ theme }) => theme.color.text_color_300}; font-size: 12px; font-weight: 500; text-align: left; white-space: nowrap;`;

const CallFactsCell = styled.td`padding: 9px 12px; color: ${({ theme }) => theme.color.text_color_100}; font-size: 13px; font-variant-numeric: tabular-nums;`;

const CallConnectDelay = styled.span`display: block; color: ${({ theme }) => theme.color.text_color_300}; margin-top: 3px; font-size: 12px;`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.name === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'};
  margin: 0;
`;

const BadgeIcon = styled.svg`
  width: 12px;
  height: 12px;
  flex: 0 0 12px;
  align-self: center;
  display: block;
`;

const StatusBadge = styled(T.Badge).attrs<{ $status: string }>(props => ({ $tone: callStatusTone(props.$status) }))<{ $status: string }>``;

const CallTypeBadge = styled(T.Badge).attrs<{ $type: string }>(props => ({ $tone: props.$type === "INBOUND" ? "info" : "neutral" }))<{ $type: string }>``;

const PlayButton = styled(Button).attrs({ $variant: "secondary" })``;

const RecordActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const CallRecordingActions = styled.div`
  position: relative;
  margin-top: auto;
  padding: 12px 16px 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 16px;
    right: 16px;
    border-top: 1px solid ${({ theme }) => theme.color.primary_300};
  }
`;

const RecordingAudio = styled.audio`
  display: block;
  width: 100%;
  height: 36px;
  margin-bottom: 10px;
  accent-color: ${({ theme }) => theme.color.point_y_100};
`;

const DownloadButton = styled(Button).attrs({ $variant: "ghost" })``;

const StereoDownloadButton = styled(Button).attrs({ $variant: "ghost" })``;

const LoadingText = styled.div`
  padding: 60px;
  text-align: center;
  color: ${({ theme }) => theme.color.text_color_300};
  font-size: 15px;
`;

const AiResultBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AiResizableArea = styled.div`
  position: relative;
  width: 100%;
  min-width: 360px;
  max-width: 100%;

  @media (max-width: 640px) {
    width: 100% !important;
    min-width: 0;
  }
`;

const AiResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: -5px;
  bottom: 0;
  width: 10px;
  cursor: col-resize;
  touch-action: none;

  &::after {
    content: '';
    position: absolute;
    top: 8px;
    right: 4px;
    bottom: 8px;
    width: 2px;
    border-radius: 2px;
    background: transparent;
    transition: background 0.15s;
  }

  &:hover::after,
  &:active::after {
    background: ${({ theme }) => theme.color.point_y_100};
  }

  @media (max-width: 640px) {
    display: none;
  }
`;

const AiResultMeta = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.color.primary_300};
  border-radius: 8px;
  background: ${({ theme }) => theme.color.primary_100};
`;

const AiMetaTable = styled.table`
  width: 100%;
  min-width: 460px;
  border-collapse: collapse;
  table-layout: fixed;
`;

const AiMetaHead = styled.th`
  padding: 8px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.color.primary_300};
  background: ${({ theme }) => theme.color.primary_50};
  color: ${({ theme }) => theme.color.text_color_300};
  font-size: 11px;
  font-weight: 700;
  text-align: left;
`;

const AiMetaCell = styled.td`
  padding: 10px 12px;
  color: ${({ theme }) => theme.color.text_color_100};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;

  &:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.color.primary_300};
  }
`;

const AiStatusBadge = styled(T.Badge).attrs<{ $status: string }>(props => ({ $tone: props.$status === "DONE" ? "success" : props.$status === "FAILED" ? "danger" : "warning" }))<{ $status: string }>``;

const AiTabBar = styled.div`
  display: flex;
  gap: 2px;
  border-bottom: 1px solid ${({ theme }) => theme.color.primary_300};
  overflow-x: auto;
`;

const AiTabButton = styled.button<{ $active: boolean }>`
  padding: 8px 12px;
  border: 0;
  border-bottom: 2px solid ${({ theme, $active }) => $active ? theme.color.point_y_100 : 'transparent'};
  background: transparent;
  color: ${({ theme, $active }) => $active ? theme.color.point_y_100 : theme.color.text_color_300};
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.point_y_100};
    outline-offset: -2px;
  }
`;

const AiTabToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
`;

const AiTabActionButton = styled(Button).attrs({ $variant: "ghost", $size: "sm" })``;

const AiTabPanel = styled.div`min-height: 160px; padding: 20px 0; min-width: 0; color: ${({ theme }) => theme.color.text_color_100};`;

const AiDialogueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 12px 8px;
`;

const AiDialogueLine = styled.div<{ $sender: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $sender }) => $sender ? 'flex-start' : 'flex-end'};
  gap: 5px;
  min-width: 0;
`;

const AiDialogueMeta = styled.div<{ $sender: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $sender }) => $sender ? 'flex-start' : 'flex-end'};
  flex-wrap: wrap;
  gap: 2px 8px;
  max-width: min(84%, 720px);
  color: ${({ theme }) => theme.color.text_color_200};
  font-size: 12px;
  text-align: ${({ $sender }) => $sender ? 'left' : 'right'};
`;

const AiDialogueSpeaker = styled.span`color: ${({ theme }) => theme.color.text_color_100}; font-weight: 600; overflow-wrap: anywhere;`;

const AiDialogueDetail = styled.span`color: ${({ theme }) => theme.color.text_color_300}; font-size: 11px; font-variant-numeric: tabular-nums;`;

const AiDialogueOriginal = styled(OriginalName)<{ $sender: boolean }>`display: block; max-width: min(84%, 720px); font-size: 11px; text-align: ${({ $sender }) => $sender ? 'left' : 'right'};`;

const AiDialogueBubble = styled.div<{ $sender: boolean }>`
  max-width: min(84%, 720px);
  padding: 11px 14px;
  border: 1px solid ${({ theme, $sender }) => $sender ? theme.color.primary_300 : theme.color.point_y_100 + '33'};
  border-radius: ${({ $sender }) => $sender ? '10px 10px 10px 3px' : '10px 10px 3px 10px'};
  background: ${({ theme, $sender }) => $sender ? theme.color.primary_50 : theme.color.point_y_100 + '12'};
  color: ${({ theme }) => theme.color.text_color_100};
  font-size: 13px;
  line-height: 1.65;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const AiSummarySection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 6px;

  & + & {
    margin-top: 16px;
  }
`;

const AiSummaryLabel = styled.h5`
  margin: 0;
  color: ${({ theme }) => theme.color.text_color_300};
  font-size: 12px;
`;

const AiSummaryValue = styled.div`
  color: ${({ theme }) => theme.color.text_color_100};
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const AiInsightList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const AiInsightItem = styled.div<{ $depth: number }>`
  padding-left: ${({ $depth }) => $depth * 12}px;
  color: ${({ theme }) => theme.color.text_color_100};
  font-size: 13px;
  line-height: 1.5;
`;

const AiAnalyzeButton = Button;

const AiResultText = styled.pre`background: ${({ theme }) => theme.color.primary_50}; color: ${({ theme }) => theme.color.text_color_100}; border-radius: 6px; padding: 16px; font-size: 12px; line-height: 1.7; white-space: pre-wrap; overflow-wrap: anywhere; margin: 0;`;

const AiResultEmpty = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.color.text_color_300};
  padding: 12px 0;
`;

// 통화 전환 섹션 스타일
const TransferFlowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const TransferNode = styled.div<{ $variant?: string }>`display: flex; flex-direction: column; gap: 4px; min-width: 0; padding: 10px 14px; border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 6px;`;

const TransferNodeLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.text_color_300};
`;

const TransferNodeName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.text_color_100};
`;

const TransferNodePhone = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.color.text_color_300};
`;

const TransferArrow = styled.div<{ $dir: 'in' | 'out' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  font-size: 20px;
  color: ${({ $dir }) => $dir === 'in' ? '#2563EB' : '#6F7787'};
  font-weight: 700;
`;

const TransferArrowLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

// 당겨짐 인라인 배지
const PickupInlineBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid #059669;
  background-color: #05966915;
  color: #059669;
  white-space: nowrap;
  vertical-align: middle;
`;

// 발신자/수신자 섹션 내 전환 인라인 배지
const TransferInlineBadge = styled.span<{ $dir: 'in' | 'out' }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid ${({ $dir }) => $dir === 'in' ? '#2563EB' : '#6F7787'};
  background-color: ${({ $dir }) => $dir === 'in' ? '#2563EB15' : '#6F778715'};
  color: ${({ $dir }) => $dir === 'in' ? '#2563EB' : '#6F7787'};
  white-space: nowrap;
  vertical-align: middle;
`;

const TransferNavBtn = styled(Button).attrs({ $variant: "ghost" })``;

// ─── 유틸 함수 ────────────────────────────────────────────────────────────────

const getCallTypeLabel = (type: string) => {
  switch (type) {
    case 'INBOUND': return '수신';
    case 'OUTBOUND': return '발신';
    case 'INTERNAL': return '내선';
    default: return type;
  }
};

const formatDuration = (seconds: number | null | undefined) => {
  if (!seconds && seconds !== 0) return '-';
  const totalSeconds = Math.round(seconds);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return m > 0 ? `${m}분 ${s}초` : `${s}초`;
};

const formatDateTime = (dt: string | null | undefined) => {
  if (!dt) return '-';
  return new Date(dt).toLocaleString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const getConnectionDelay = (ringingAt: string | null | undefined, dialupAt: string | null | undefined) => {
  if (!ringingAt || !dialupAt) return null;
  const elapsed = Math.floor((new Date(dialupAt).getTime() - new Date(ringingAt).getTime()) / 1000);
  return Number.isFinite(elapsed) && elapsed >= 0 ? `${elapsed}초 뒤에 연결됨` : null;
};

const formatTransferPerson = (username: string | null | undefined, phone: string | null | undefined): string => {
  if (!username && !phone) return '';
  if (username && phone) return `${username} (${phone})`;
  return username || phone || '';
};

type AiTab = 'script' | 'summary' | 'raw';
type AiDialogue = { speaker: string; time: string; text: string };
type AiInsightLine = { label: string; value: string; depth: number };

const aiTabs: { value: AiTab; label: string }[] = [
  { value: 'script', label: '스크립트' },
  { value: 'summary', label: '통화요약' },
  { value: 'raw', label: 'JSON 원본' },
];

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const asText = (value: unknown) => typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value).trim() : '';

const insightLabels: Record<string, string> = {
  sentiment: '감정', intent: '고객 의도', emotion: '감정 변화', keywords: '주요 키워드',
  topics: '주요 주제', pain_points: '불편 사항', needs: '요청 사항', action_items: '후속 조치',
  customer_profile: '고객 특성', recommendation: '권장 사항', score: '점수', reason: '근거',
  current_call_purpose: '이번 통화 목적', post_call_behavior: '통화 후 예정 행동',
  anticipated_next_call: '다음 통화 예상', confidence: '예측 신뢰도', expected_timing: '예상 시점',
  expected_purpose: '예상 목적', recommended_opening: '권장 시작 문구', evidence: '근거',
};

const getInsightLines = (value: unknown, depth = 0, label?: string): AiInsightLine[] => {
  const text = asText(value);
  if (text && label) return [{ label, value: text, depth }];
  if (Array.isArray(value)) return value.flatMap((item, index) => getInsightLines(item, depth, `항목 ${index + 1}`));
  if (!isObject(value)) return [];
  return Object.entries(value).flatMap(([key, item], index) => {
    const itemLabel = insightLabels[key] || `세부 항목 ${index + 1}`;
    return isObject(item) || Array.isArray(item)
      ? [{ label: itemLabel, value: '', depth }, ...getInsightLines(item, depth + 1)]
      : getInsightLines(item, depth, itemLabel);
  });
};

const getAiDialogues = (parsed: Record<string, unknown>): AiDialogue[] => {
  const data = isObject(parsed.data) ? parsed.data : {};
  return Array.isArray(data.call_dialogue) ? data.call_dialogue.flatMap((item): AiDialogue[] => {
    if (!isObject(item)) return [];
    const text = asText(item.text);
    return text ? [{ speaker: asText(item.speaker) || '참여자', time: asText(item.time) || '시간 정보 없음', text }] : [];
  }) : [];
};

const getAiSummary = (parsed: Record<string, unknown>) => {
  const data = isObject(parsed.data) ? parsed.data : {};
  const analysis = isObject(data.analysis_data) ? data.analysis_data : {};
  const summary = isObject(analysis.summary) ? analysis.summary : {};
  const oneLine = asText(summary.one_line);
  const fullSummary = asText(summary.full_summary);
  const insightLines = getInsightLines(analysis.cbi_insight);
  const text = [
    oneLine && `한 줄 요약: ${oneLine}`,
    fullSummary && `상세 요약: ${fullSummary}`,
    insightLines.length > 0 && 'CBI 인사이트:',
    ...insightLines.map(({ label, value, depth }) => `${'  '.repeat(depth)}${label}${value ? `: ${value}` : ''}`),
  ].filter(Boolean).join('\n');
  return { oneLine, fullSummary, insightLines, text };
};

// ─── 메인 컴포넌트 ────────────────────────────────────────────────────────────

interface AiAnalysisResult {
  status: string;
  resultText: string | null;
  aiModel: string | null;
  analysisType: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

const CallHistoryDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { mode, user } = useAuth();
  const isCoreMode = mode === 'core';
  const canAnalyze = mode === 'basic' && user?.roleList?.includes('SUPER_ADMIN');
  const { data, loading, error, request } = useApi();
  const { data: aiData, loading: aiLoading, request: aiRequest } = useApi();
  const { loading: aiAnalyzeLoading, request: aiAnalyzeRequest } = useApi();
  const [playbackInfo, setPlaybackInfo] = useState<{ url: string; name: string } | null>(null);
  const [aiTab, setAiTab] = useState<AiTab>('script');
  const [aiResultWidth, setAiResultWidth] = useState<number | null>(null);
  const aiResultRef = useRef<HTMLDivElement>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [isDownloadingRecord, setIsDownloadingRecord] = useState(false);
  const [isDownloadingStereoRecord, setIsDownloadingStereoRecord] = useState(false);
  const recordAudioRef = useRef<HTMLAudioElement | null>(null);
  const [recordDuration, setRecordDuration] = useState<number | null>(null);

  const handleAiAnalyze = async () => {
    if (!canAnalyze) return;
    try {
      await aiAnalyzeRequest({ url: '/ai-analyze', method: 'POST', params: { callId: Number(id) } });
      toast.success('AI 분석 요청이 완료되었습니다.');
      aiRequest({ url: '/call/ai/result', method: 'GET', params: { callId: Number(id), analysisType: 'TOTAL_DATA' } });
    } catch {
      toast.error('AI 분석 요청에 실패했습니다.');
    }
  };

  const handlePlay = async (call: any) => {
    if (isRecordActionDisabled(call)) return;

    if (playbackInfo) {
      recordAudioRef.current?.pause();
      setPlaybackInfo(null);
      setRecordDuration(null);
      return;
    }
    try {
      setIsLoadingRecord(true);
      const blob = await downloadRecordFile(Number(id));
      const url = URL.createObjectURL(blob);
      const name = buildRecordFileName(call, blob);
      setRecordDuration(null);
      setPlaybackInfo({ url, name });
    } catch (error) {
      toast.error('녹취 파일을 재생할 수 없습니다.');
    } finally {
      setIsLoadingRecord(false);
    }
  };

  const handleDownload = async (call: any) => {
    if (isRecordActionDisabled(call)) return;

    try {
      setIsDownloadingRecord(true);
      const blob = await downloadRecordFile(Number(id));
      saveBlobAsFile(blob, buildRecordFileName(call, blob));
      toast.success('녹취 파일 다운로드를 시작했습니다.');
    } catch (error) {
      toast.error('녹취 파일을 다운로드할 수 없습니다.');
    } finally {
      setIsDownloadingRecord(false);
    }
  };

  const handleDownloadStereo = async (call: any) => {
    if (isRecordActionDisabled(call)) return;

    try {
      setIsDownloadingStereoRecord(true);
      const blob = await downloadStereoRecordFile(Number(id));
      saveBlobAsFile(blob, buildStereoRecordFileName(call, blob));
      toast.success('스테레오 녹취 파일 다운로드를 시작했습니다.');
    } catch (error) {
      toast.error('스테레오 녹취 파일을 다운로드할 수 없습니다.');
    } finally {
      setIsDownloadingStereoRecord(false);
    }
  };

  useEffect(() => {
    if (id) {
      setPlaybackInfo(null);
      setRecordDuration(null);
      request({ url: `/call/log/${id}`, method: 'GET' });
      if (!isCoreMode) {
        aiRequest({ url: '/call/ai/result', method: 'GET', params: { callId: Number(id), analysisType: 'TOTAL_DATA' } });
      }
    }
  }, [id, isCoreMode, request, aiRequest]);

  useEffect(() => () => {
    if (playbackInfo) URL.revokeObjectURL(playbackInfo.url);
  }, [playbackInfo]);

  const call = data as any;
  const aiResult = aiData as AiAnalysisResult | null;
  const recordActionDisabled = call ? isRecordActionDisabled(call) : false;
  let parsedAiResult: Record<string, unknown> | null = null;
  if (aiResult?.resultText) {
    try {
      const parsed = JSON.parse(aiResult.resultText);
      parsedAiResult = isObject(parsed) ? parsed : null;
    } catch {}
  }
  const dialogues = parsedAiResult ? getAiDialogues(parsedAiResult) : [];
  const summary = parsedAiResult ? getAiSummary(parsedAiResult) : { oneLine: '', fullSummary: '', insightLines: [], text: '' };
  const senderName = call?.senderName || '미등록자';
  const receiverName = call?.receiverName || '미등록자';
  const realSenderName = mode === 'basic' ? call?.realSenderName : null;
  const realReceiverName = mode === 'basic' ? call?.realReceiverName : null;
  const connectionDelay = getConnectionDelay(call?.ringingAt, call?.dialupAt);
  const tabText = parsedAiResult ? {
    script: dialogues.map(({ time, speaker, text }) => `[${time}] ${/수신자|receiver|callee/i.test(speaker) ? receiverName : senderName}: ${text}`).join('\n'),
    summary: summary.text,
    raw: JSON.stringify(parsedAiResult, null, 2),
  } : null;
  const activeTabText = tabText?.[aiTab] || '';

  const copyAiTab = async () => {
    if (!activeTabText || !navigator.clipboard) return toast.error('복사할 내용이 없습니다.');
    try {
      await navigator.clipboard.writeText(activeTabText);
      toast.success('내용을 복사했습니다.');
    } catch {
      toast.error('복사에 실패했습니다.');
    }
  };

  const downloadAiTab = () => {
    if (!activeTabText) return;
    saveBlobAsFile(new Blob([`\uFEFF${activeTabText}`], { type: 'text/plain;charset=utf-8' }), `${id || 'unknown'}-ai-${aiTab}.txt`);
  };

  const handleAiTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, currentTab: AiTab) => {
    const currentIndex = aiTabs.findIndex(({ value }) => value === currentTab);
    const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? aiTabs.length - 1 :
      event.key === 'ArrowRight' ? (currentIndex + 1) % aiTabs.length :
      event.key === 'ArrowLeft' ? (currentIndex + aiTabs.length - 1) % aiTabs.length : -1;
    if (nextIndex < 0) return;
    event.preventDefault();
    const nextTab = aiTabs[nextIndex].value;
    setAiTab(nextTab);
    document.getElementById(`ai-result-tab-${nextTab}`)?.focus();
  };

  const handleAiResizeStart = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = aiResultRef.current?.getBoundingClientRect().width || 640;
    const handleMove = (moveEvent: MouseEvent) => {
      setAiResultWidth(Math.max(360, Math.min(aiResultRef.current?.parentElement?.clientWidth || window.innerWidth - 96, startWidth + moveEvent.clientX - startX)));
    };
    const handleEnd = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
  };

  return (
    <PageContainer>
      <PageHeader title="통화 이력 상세" actions={<BackButton onClick={() => navigate(-1)}>← 목록으로</BackButton>} />

      {loading ? (
        <PageState status="loading" />
      ) : error ? <PageState status="error" onRetry={() => request({ url: `/call/log/${id}`, method: 'GET' })} /> : call ? (
        <Card>
          <HeroSection>
            <HeroHeader>
              <div>
                <SectionTitle>통화 정보</SectionTitle>
              </div>
              <BadgeRow>
                <CallTypeBadge $type={call.callType}>
                  {call.callType === 'INBOUND' && <BadgeIcon aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 5 5 19M5 10v9h9" /></BadgeIcon>}
                  {call.callType === 'OUTBOUND' && <BadgeIcon aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 19 19 5M10 5h9v9" /></BadgeIcon>}
                  {getCallTypeLabel(call.callType)}
                </CallTypeBadge>
                <StatusBadge $status={call.callStatus}>
                  {call.callStatus === 'MISSED' && <BadgeIcon aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m16 2 6 6" /><path d="m22 2-6 6" /><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" /></BadgeIcon>}
                  {getCallStatusLabel(call.callStatus)}
                </StatusBadge>
              </BadgeRow>
            </HeroHeader>

            <CallInfoLayout>
              <CallInfoLeft data-testid="call-info-left">
                <CallRoute>
                <ParticipantBlock data-testid="call-sender">
                  <ParticipantLabel>발신자</ParticipantLabel>
                  <ParticipantIdentity>
                    {realSenderName ? <>
                      <ParticipantName><RealContactName name={realSenderName} /></ParticipantName>
                      <OriginalName>{call.senderName || '미등록자'}</OriginalName>
                    </> : <ParticipantName>{call.senderName || '미등록자'}</ParticipantName>}
                    <ParticipantPhone>{formatPhoneNumber(call.senderPhone) || '-'}</ParticipantPhone>
                  </ParticipantIdentity>
                  <div>
                    {call.transferPhone && call.transferPhone === call.senderPhone && (
                      <TransferInlineBadge $dir="out">→ 돌려줌 {formatTransferPerson(call.transfereeUsername, call.transfereePhone)}</TransferInlineBadge>
                    )}
                  </div>
                </ParticipantBlock>

                <RouteConnector>
                  <RouteArrow>→</RouteArrow>
                </RouteConnector>

                <ParticipantBlock data-testid="call-receiver">
                  <ParticipantLabel>수신자</ParticipantLabel>
                  <ParticipantIdentity>
                    {realReceiverName ? <>
                      <ParticipantName><RealContactName name={realReceiverName} /></ParticipantName>
                      <OriginalName>{call.receiverName || '미등록자'}</OriginalName>
                    </> : <ParticipantName>{call.receiverName || '미등록자'}</ParticipantName>}
                    <ParticipantPhone>{formatPhoneNumber(call.receiverPhone) || '-'}</ParticipantPhone>
                  </ParticipantIdentity>
                  <div>
                    {call.transfererPhone && (
                      <TransferInlineBadge $dir="in">← 돌려받음</TransferInlineBadge>
                    )}
                    {call.pickupTargetPhone && (
                      <PickupInlineBadge title={`${call.pickupTargetUsername ? `${call.pickupTargetUsername} (${call.pickupTargetPhone})` : call.pickupTargetPhone}으로 부터 당겨짐`}>
                        ↙ 당겨짐
                      </PickupInlineBadge>
                    )}
                    {call.transferPhone && call.transferPhone === call.receiverPhone && (
                      <TransferInlineBadge $dir="out">→ 돌려줌 {formatTransferPerson(call.transfereeUsername, call.transfereePhone)}</TransferInlineBadge>
                    )}
                  </div>
                </ParticipantBlock>
                </CallRoute>

                <CallRecordingActions>
                  {playbackInfo && (
                    <RecordingAudio
                      ref={recordAudioRef}
                      data-testid="recording-audio"
                      aria-label="통화 녹취 재생바"
                      controls
                      controlsList="nodownload noremoteplayback"
                      autoPlay
                      preload="none"
                      src={playbackInfo.url}
                      onLoadedMetadata={(event) => {
                        const duration = event.currentTarget.duration;
                        if (Number.isFinite(duration)) setRecordDuration(duration);
                      }}
                    />
                  )}

                  <RecordActionRow>
                    <PlayButton
                      onClick={() => handlePlay(call)}
                      disabled={recordActionDisabled || isLoadingRecord}
                      title={recordActionDisabled ? '부재중 통화는 녹취 파일을 사용할 수 없습니다.' : undefined}
                    >
                      {isLoadingRecord ? '로딩 중...' : playbackInfo ? '⏹ 재생 중지' : '▶ 녹취 파일 재생'}
                    </PlayButton>
                    <DownloadButton
                      onClick={() => handleDownload(call)}
                      disabled={recordActionDisabled || isDownloadingRecord}
                      title={recordActionDisabled ? '부재중 통화는 녹취 파일을 사용할 수 없습니다.' : undefined}
                    >
                      {isDownloadingRecord ? '다운로드 중...' : '⬇ 녹취 파일 다운로드'}
                    </DownloadButton>
                    <StereoDownloadButton
                      onClick={() => handleDownloadStereo(call)}
                      disabled={recordActionDisabled || isDownloadingStereoRecord}
                      title={recordActionDisabled ? '부재중 통화는 녹취 파일을 사용할 수 없습니다.' : 'RX/TX 녹취를 좌우 채널로 합친 wav 파일을 다운로드합니다.'}
                    >
                      {isDownloadingStereoRecord ? '다운로드 중...' : '⬇ 스테레오 다운로드'}
                    </StereoDownloadButton>
                  </RecordActionRow>
                </CallRecordingActions>

              </CallInfoLeft>

              <CallFacts>
                <CallFactsTable aria-label="통화 정보 필드">
                  <tbody>
                    <tr><CallFactsHead scope="row">입력/유입 번호</CallFactsHead><CallFactsCell>{formatPhoneNumber(call.callNumber) || '-'}</CallFactsCell></tr>
                    <tr><CallFactsHead scope="row">통화 시간</CallFactsHead><CallFactsCell>{formatDuration(recordDuration ?? call.callTime)}</CallFactsCell></tr>
                    <tr><CallFactsHead scope="row">벨 울림</CallFactsHead><CallFactsCell>{formatDateTime(call.ringingAt)}</CallFactsCell></tr>
                    <tr><CallFactsHead scope="row">통화 연결</CallFactsHead><CallFactsCell>{formatDateTime(call.dialupAt)}{connectionDelay && <CallConnectDelay>{connectionDelay}</CallConnectDelay>}</CallFactsCell></tr>
                    <tr><CallFactsHead scope="row">통화 종료</CallFactsHead><CallFactsCell>{formatDateTime(call.hangupAt)}</CallFactsCell></tr>
                  </tbody>
                </CallFactsTable>
              </CallFacts>
            </CallInfoLayout>

          </HeroSection>

          {/* 통화 전환 정보 - transferer, transferee, transfer, pickup 중 하나라도 있을 때만 표시 */}
          {(call.transfererPhone || call.transfereePhone || call.transferPhone || call.pickupTargetPhone) && (
            <>
              <Divider />
              <div>
                <SectionTitle>통화 전환 정보</SectionTitle>
                {/* 돌려받음: 직전 전달자 → 수신자(receiver) */}
                {call.transfererPhone && (
                  <TransferFlowRow>
                    <TransferNode $variant="from">
                      <TransferNodeLabel style={{ color: '#2563EB' }}>직전 전달자</TransferNodeLabel>
                      <TransferNodeName>
                        {call.transfererUsername || call.transfererPhone}
                      </TransferNodeName>
                      {call.transfererUsername && (
                        <TransferNodePhone>{formatPhoneNumber(call.transfererPhone)}</TransferNodePhone>
                      )}
                    </TransferNode>
                    <TransferArrow $dir="in">
                      <span>→</span>
                      <TransferArrowLabel style={{ color: '#2563EB' }}>돌려받음</TransferArrowLabel>
                    </TransferArrow>
                    <TransferNode $variant="from">
                      <TransferNodeLabel style={{ color: '#2563EB' }}>받은 사람</TransferNodeLabel>
                      <TransferNodeName>
                        {call.receiverName || call.receiverPhone || '-'}
                      </TransferNodeName>
                      {call.receiverName && call.receiverPhone && (
                        <TransferNodePhone>{formatPhoneNumber(call.receiverPhone)}</TransferNodePhone>
                      )}
                    </TransferNode>
                  </TransferFlowRow>
                )}

                {/* 돌려줌: 돌려준 주체 → 다음 전달 대상 */}
                {call.transferPhone && (
                  <TransferFlowRow style={{ marginTop: call.transfererPhone ? '12px' : '0' }}>
                    <TransferNode $variant="current">
                      <TransferNodeLabel>돌려준 주체</TransferNodeLabel>
                      <TransferNodeName>
                        {call.transferUsername || call.transferPhone || '-'}
                      </TransferNodeName>
                      {call.transferUsername && call.transferPhone && (
                        <TransferNodePhone>{formatPhoneNumber(call.transferPhone)}</TransferNodePhone>
                      )}
                    </TransferNode>
                    {call.transfereePhone && (
                      <>
                        <TransferArrow $dir="out">
                          <span>→</span>
                          <TransferArrowLabel style={{ color: '#6F7787' }}>전달</TransferArrowLabel>
                        </TransferArrow>
                        <TransferNode $variant="to">
                          <TransferNodeLabel style={{ color: '#6F7787' }}>다음 전달 대상</TransferNodeLabel>
                          <TransferNodeName>
                            {call.transfereeUsername || call.transfereePhone}
                          </TransferNodeName>
                          {call.transfereeUsername && (
                            <TransferNodePhone>{formatPhoneNumber(call.transfereePhone)}</TransferNodePhone>
                          )}
                        </TransferNode>
                      </>
                    )}
                  </TransferFlowRow>
                )}

                {/* 당겨짐 */}
                {call.pickupTargetPhone && (
                  <TransferFlowRow style={{ marginTop: (call.transfererPhone || call.transferPhone) ? '12px' : '0' }}>
                    <TransferNode $variant="from">
                      <TransferNodeLabel style={{ color: '#059669' }}>당겨진 대상</TransferNodeLabel>
                      <TransferNodeName>
                        {call.pickupTargetUsername || call.pickupTargetPhone}
                      </TransferNodeName>
                      {call.pickupTargetUsername && (
                        <TransferNodePhone>{formatPhoneNumber(call.pickupTargetPhone)}</TransferNodePhone>
                      )}
                    </TransferNode>
                    <TransferArrow $dir="in">
                      <span>→</span>
                      <TransferArrowLabel style={{ color: '#059669' }}>당겨짐</TransferArrowLabel>
                    </TransferArrow>
                    <TransferNode $variant="from">
                      <TransferNodeLabel style={{ color: '#059669' }}>받은 사람</TransferNodeLabel>
                      <TransferNodeName>
                        {call.receiverName || call.receiverPhone || '-'}
                      </TransferNodeName>
                      {call.receiverName && call.receiverPhone && (
                        <TransferNodePhone>{formatPhoneNumber(call.receiverPhone)}</TransferNodePhone>
                      )}
                    </TransferNode>
                  </TransferFlowRow>
                )}

                {/* 직전 / 다음 통화 이동 버튼 */}
                <div style={{ display: 'flex', gap: '10px', marginTop: '16px', flexWrap: 'wrap' }}>
                  {call.transfererCallId && (
                    <TransferNavBtn onClick={() => navigate(`/statistics/call-history/${call.transfererCallId}`)}>
                      ← 직전 통화 보기&nbsp;
                      <span style={{ opacity: 0.6, fontSize: '12px' }}>
                        ({formatTransferPerson(call.transfererUsername, call.transfererPhone)})
                      </span>
                    </TransferNavBtn>
                  )}
                  {call.transfereeCallId && (
                    <TransferNavBtn onClick={() => navigate(`/statistics/call-history/${call.transfereeCallId}`)}>
                      다음 통화 보기 →&nbsp;
                      <span style={{ opacity: 0.6, fontSize: '12px' }}>
                        ({formatTransferPerson(call.transfereeUsername, call.transfereePhone)})
                      </span>
                    </TransferNavBtn>
                  )}
                </div>
              </div>
            </>
          )}

          {!isCoreMode && <>
          <Divider />

          {/* AI 분석 결과 */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <SectionTitle style={{ margin: 0 }}>AI 분석 결과</SectionTitle>
              {canAnalyze && (
                <AiAnalyzeButton onClick={handleAiAnalyze} disabled={aiAnalyzeLoading}>
                  {aiAnalyzeLoading ? '분석 중...' : 'AI 분석'}
                </AiAnalyzeButton>
              )}
            </div>
            {aiLoading ? (
              <LoadingText style={{ padding: '16px 0' }}>AI 분석 결과 로딩 중...</LoadingText>
            ) : aiResult ? (
              <AiResultBox>
                <AiResultMeta>
                  <AiMetaTable aria-label="AI 분석 메타 정보">
                    <thead>
                      <tr>
                        <AiMetaHead>분석 상태</AiMetaHead>
                        {aiResult.aiModel && <AiMetaHead>AI 모델</AiMetaHead>}
                        {aiResult.updatedAt && <AiMetaHead>분석 일시</AiMetaHead>}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <AiMetaCell>
                          <AiStatusBadge $status={aiResult.status}>
                            {aiResult.status === 'DONE' ? '완료' : aiResult.status === 'WAIT' ? '대기' : aiResult.status === 'FAILED' ? '실패' : aiResult.status}
                          </AiStatusBadge>
                        </AiMetaCell>
                        {aiResult.aiModel && <AiMetaCell>{aiResult.aiModel}</AiMetaCell>}
                        {aiResult.updatedAt && <AiMetaCell>{formatDateTime(aiResult.updatedAt)}</AiMetaCell>}
                      </tr>
                    </tbody>
                  </AiMetaTable>
                </AiResultMeta>
                {parsedAiResult && tabText && (
                  <div>
                    <AiTabBar role="tablist" aria-label="AI 분석 결과">
                      {aiTabs.map(({ value: tab, label }) => (
                        <AiTabButton key={tab} id={`ai-result-tab-${tab}`} type="button" role="tab" aria-controls="ai-result-panel" aria-selected={aiTab === tab} tabIndex={aiTab === tab ? 0 : -1} $active={aiTab === tab} onClick={() => setAiTab(tab)} onKeyDown={(event) => handleAiTabKeyDown(event, tab)}>
                          {label}
                        </AiTabButton>
                      ))}
                    </AiTabBar>
                    <AiTabToolbar style={{ margin: '10px 0' }}>
                      <AiTabActionButton type="button" aria-label={`${aiTab === 'script' ? '스크립트' : aiTab === 'summary' ? '통화요약' : 'JSON 원본'} 복사`} onClick={copyAiTab} disabled={!activeTabText}>복사</AiTabActionButton>
                      <AiTabActionButton type="button" aria-label={`${aiTab === 'script' ? '스크립트' : aiTab === 'summary' ? '통화요약' : 'JSON 원본'} 다운로드`} onClick={downloadAiTab} disabled={!activeTabText}>.txt 다운로드</AiTabActionButton>
                    </AiTabToolbar>
                    <AiResizableArea ref={aiResultRef} data-testid="ai-result-resizable-area" style={aiResultWidth ? { width: aiResultWidth } : undefined}>
                    <AiTabPanel id="ai-result-panel" role="tabpanel" aria-labelledby={`ai-result-tab-${aiTab}`}>
                      {aiTab === 'script' && (dialogues.length > 0 ? (
                        <AiDialogueList role="list" aria-label="전사 스크립트">
                          {dialogues.map((dialogue, index) => {
                            const sender = !/수신자|receiver|callee/i.test(dialogue.speaker);
                            const participant = sender ? senderName : receiverName;
                            const realName = sender ? realSenderName : realReceiverName;
                            const phone = formatPhoneNumber(sender ? call.senderPhone : call.receiverPhone) || '-';
                            return <AiDialogueLine key={`${dialogue.time}-${index}`} role="listitem" $sender={sender}>
                              <AiDialogueMeta $sender={sender}>
                                <AiDialogueSpeaker>{realName ? <RealContactName name={realName} /> : participant}</AiDialogueSpeaker>
                                <AiDialogueDetail>{phone}</AiDialogueDetail>
                                <AiDialogueDetail>{dialogue.time}</AiDialogueDetail>
                              </AiDialogueMeta>
                              {realName && <AiDialogueOriginal $sender={sender}>{participant}</AiDialogueOriginal>}
                              <AiDialogueBubble $sender={sender}>{dialogue.text}</AiDialogueBubble>
                            </AiDialogueLine>;
                          })}
                        </AiDialogueList>
                      ) : <AiResultEmpty>스크립트 내용이 없습니다.</AiResultEmpty>)}
                      {aiTab === 'summary' && (summary.text ? <>
                        {summary.oneLine && <AiSummarySection><AiSummaryLabel>한 줄 요약</AiSummaryLabel><AiSummaryValue>{summary.oneLine}</AiSummaryValue></AiSummarySection>}
                        {summary.fullSummary && <AiSummarySection><AiSummaryLabel>상세 요약</AiSummaryLabel><AiSummaryValue>{summary.fullSummary}</AiSummaryValue></AiSummarySection>}
                        {summary.insightLines.length > 0 && <AiSummarySection><AiSummaryLabel>CBI 인사이트</AiSummaryLabel><AiInsightList>{summary.insightLines.map((line, index) => <AiInsightItem key={`${line.label}-${index}`} $depth={line.depth}><strong>{line.label}</strong>{line.value && `: ${line.value}`}</AiInsightItem>)}</AiInsightList></AiSummarySection>}
                      </> : <AiResultEmpty>통화요약 내용이 없습니다.</AiResultEmpty>)}
                      {aiTab === 'raw' && <AiResultText>{tabText.raw}</AiResultText>}
                    </AiTabPanel>
                    <AiResizeHandle role="separator" aria-label="AI 분석 영역 너비 조절" aria-orientation="vertical" aria-valuemin={360} aria-valuenow={aiResultWidth || 640} tabIndex={0} onMouseDown={handleAiResizeStart} onKeyDown={event => {
                      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                        event.preventDefault();
                        const maxWidth = aiResultRef.current?.parentElement?.clientWidth || window.innerWidth - 96;
                        setAiResultWidth(width => Math.max(360, Math.min(maxWidth, (width || 640) + (event.key === 'ArrowRight' ? 24 : -24))));
                      }
                    }} />
                    </AiResizableArea>
                  </div>
                )}
                {aiResult.resultText && !parsedAiResult && <AiResultText>{aiResult.resultText}</AiResultText>}
                {!aiResult.resultText && aiResult.status === 'DONE' && (
                  <AiResultEmpty>분석 결과 내용이 없습니다.</AiResultEmpty>
                )}
                {aiResult.status !== 'DONE' && (
                  <AiResultEmpty>
                    {aiResult.status === 'WAIT' ? 'AI 분석이 진행 중입니다.' : aiResult.status === 'FAILED' ? 'AI 분석에 실패했습니다.' : 'AI 분석 결과를 불러올 수 없습니다.'}
                  </AiResultEmpty>
                )}
              </AiResultBox>
            ) : (
              <AiResultEmpty>AI 분석 결과가 없습니다.</AiResultEmpty>
            )}
          </div>
          </>}

        </Card>
      ) : (
        <PageState status="empty" title="통화 정보를 찾을 수 없습니다." />
      )}
    </PageContainer>
  );
};

export default CallHistoryDetailPage;
